import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { getStyle } from '@coreui/coreui/dist/js/coreui-utilities';
import { Chart, ChartDataSets, ChartOptions, ChartType } from 'chart.js';
import * as pluginAnnotations from 'chartjs-plugin-annotation';
import moment from 'moment';
import { BaseChartDirective, Color, Label } from 'ng2-charts';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, map, skip } from 'rxjs/operators';
import { LanguageService } from '../../language.service';
import { GroupsComService } from '../../services/groups-com.service';
import { OverallStatsComService } from '../../services/overall-stats-com.service';
import { OverallStatsService } from '../../services/overallStats';
import { PdfExporterService } from '../pdf-exporter/pdf-exporter.service';

interface Group {
  value?: string;
  viewValue?: string;
  latLon?: { latitude: number, longitude: number };
  sensors?: any[];
  latLonList?: any[];
}

export type PeriodType = 'monthly' | 'weekly';

@Component({
  selector: 'app-chart-wrapper',
  templateUrl: './chart-wrapper.component.html',
  styleUrls: ['./chart-wrapper.component.scss']
})
export class ChartWrapperComponent implements OnInit, OnDestroy {
  @ViewChild(BaseChartDirective, { static: true }) chart: BaseChartDirective;

  @Input() set type(type: PeriodType) {
    if (type === 'monthly') {
      this.title = 'DASHBOARD.CARD_TITLES.AOT'
    } else {
      this.title = 'DASHBOARD.CARD_TITLES.AOTPW'
    }
    this.chartType = type;
    this.pdfChartType = `${this.chartType}-chart`
  }

  isChartLoaded: boolean = false;

  public isChartLoaded$: BehaviorSubject<boolean> = new BehaviorSubject(false)

  private readonly saveToPNGPlugin = {
    id: 'savetopng',
    isChartLoaded: false,
    afterRender: (chart, options) => {
      setTimeout(() => {
        if (chart.id && chart.height) {
          this.saveToPNGPlugin.isChartLoaded = true;
        }
      });
    }
  };

  public chartType: PeriodType;
  public pdfChartType: string;
  public title: string = 'Default'

  public lineChartData: ChartDataSets[] = [
    { data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0] }
  ];
  public lineChartLabels: Label[] = moment.months();
  public lineChartOptions: (ChartOptions)
  public lineChartColors: Color[] = [
    {
      backgroundColor: getStyle('--primary'),
      borderColor: 'rgba(255,255,255,.55)'
    }
  ];
  public lineChartLegend = false;
  public lineChartType: ChartType = 'line';
  public lineChartPlugins = [pluginAnnotations];

  public years: string[] = ['2020', '2021'];
  public selectedYear = 'allyears';
  public groups$: Observable<Group[]>;
  public selectedGroup = 'allgroups';

  private langSub: Subscription;
  private overallStatsSub: Subscription;
  public InputForm1: FormGroup;

  private yearIsSelected: boolean = false;
  private groupIsSelected: boolean = false;

  public isLoading: boolean = true;

  public noData: boolean = false;

  public clickedPDFExporterButton: boolean = false;

  private readonly chartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 0
    },
    scales: {
      yAxes: [{
        gridLines: {
          display: false,
          color: 'transparent',
          zeroLineColor: 'transparent'
        },
        ticks: {
          beginAtZero: false,
          fontSize: 12,
          fontColor: 'white',
          source: 'data',
          autoSkip: true,
          minRotation: 0,
          maxRotation: 0,
        }
      }],
      xAxes: [{
        gridLines: {
          display: false,
          color: 'transparent',
          zeroLineColor: 'transparent'
        },
        ticks: {
          beginAtZero: true,
          fontSize: 14,
          fontColor: 'white',
          source: 'data',
          autoSkip: true,
          minRotation: 0,
          maxRotation: 0,
        }
      }],
    },
    elements: {
      line: {
        borderWidth: 1
      },
      point: {
        radius: 3,
        hitRadius: 10,
        hoverRadius: 4,
      },
    },
    plugins: { datalabels: { display: false }, savetopng: { filename: 'chart-foo.png' } },

  };

  public loadingSpinner$: Observable<boolean>;

  private loadingSpinnerSub$: Subscription;

  public proceedWithPDF = undefined;

  constructor(
    private pdfExporterService: PdfExporterService,
    private overallStatsComService: OverallStatsComService,
    private cdr: ChangeDetectorRef,
    private groupsComService: GroupsComService,
    private _formBuilder: FormBuilder,
    private translateService: LanguageService,
    private overallStats: OverallStatsService) {
    let currentYear: string = moment().year().toString()
    if (!this.years.includes(currentYear)) {
      this.years.push(currentYear)
    }

    Chart.pluginService.register(this.saveToPNGPlugin)

    this.lineChartOptions = { ...this.chartOptions }
  }

  ngOnInit() {
    this.loadingSpinner$ = this.pdfExporterService.loadSpinner;
    this.groups$ = this.groupsComService.getGroups;

    this.initFormGroup();

    this.getOverallStats('allyears', 'allgroups');

    this.languageTranslations();

    this.changesInFormControl();

    this.isChartLoaded = this.saveToPNGPlugin.isChartLoaded;

    this.changeChartConfig();
  }


  clicking(): void {
    this.isChartLoaded = this.saveToPNGPlugin.isChartLoaded;
    this.handleLoadingSpinnerSubscription();
  }
  
  changeChartConfig(): void {
    this.updateChartOptions();
    this.clicking();
  }

  private updateChartOptions(): void {
    this.lineChartOptions.scales.yAxes = [{
      display: false,
      gridLines: {
        display: false,
        color: 'transparent',
        zeroLineColor: 'transparent'
      },
      ticks: {
        beginAtZero: false,
        fontSize: 12,
        fontColor: 'white',
        source: 'data',
        autoSkip: true,
        minRotation: 0,
        maxRotation: 0,
      }
    }];
  
    this.lineChartOptions.plugins = {
      datalabels: {
        anchor: 'center',
        align: 'bottom',
        color: 'white',
        rotation: 0,
        font: { size: 13, weight: 600 },
        formatter: (value, context) => value
      }
    };
  
    this.lineChartOptions.scales.xAxes = [{
      gridLines: {
        display: false,
        color: 'transparent',
        zeroLineColor: 'transparent'
      },
      ticks: {
        beginAtZero: true,
        fontSize: 13,
        padding: 18,
        fontColor: 'white',
        source: 'data',
        autoSkip: true,
        minRotation: 0,
        maxRotation: 0,
      }
    }];
  
    this.lineChartOptions = { ...this.lineChartOptions };
  }

  private handleLoadingSpinnerSubscription(): void {
    this.loadingSpinnerSub$ = this.loadingSpinner$.pipe(skip(1)).subscribe(res => {
      if (res === true && this.isChartLoaded) {
        this.loadingSpinnerSub$.unsubscribe();
        this.lineChartOptions = { ...this.chartOptions };
        this.isChartLoaded = this.saveToPNGPlugin.isChartLoaded = false;
      }
    }, () => { console.log(`Successfully ended subscription`); });
  }

  getOverallStats(year?: string, group?: string): void {
    this.noData = false;
    let object = { allYears: null, allGroups: null, year: null, group: null }
    if (year === 'allyears') object.allYears = 'True'
    if (group === 'allgroups') object.allGroups = 'True'
    if (year !== 'allyears') object.year = year;
    if (group !== 'allgroups') object.group = group;
    this.overallStatsSub = this.overallStats.getOverallStats(object)
      .pipe(
        distinctUntilChanged(),
        map(stats => ({
          monthly: stats.length && stats[0]['attributes']['monthly'].map(item => Number(item) < 1 ? Number(((item) * 100).toFixed(2)) : Number(item)),
          weekly: stats.length && stats[0]['attributes']['weekly'].map(item => Number(item) < 1 ? Number(((item) * 100).toFixed(2)) : Number(item)),
          timestamp: stats.length && stats[0]['attributes']['timestamp'],
          year: stats.length && stats[0]['attributes']['year']
        }))
      ).subscribe(stats => {
        this.updateChartData(stats);
      });
  }
  

  updateChartData(stats: any): void {
    if (this.chart) this.chart.chart.destroy();
  
    if (this.chartType === 'monthly') {
      if (stats.monthly !== undefined && !stats.monthly.length) {
        this.lineChartData[0].data = Array(12).fill(0);
        this.noData = true;
      } else {
        this.lineChartData[0].data = stats['monthly'];
      }
    } else {
      if (stats.weekly !== undefined && !stats.weekly.length) {
        this.lineChartData[0].data = Array(7).fill(0);
        this.noData = true;
      } else {
        this.lineChartData[0].data = stats['weekly'];
      }
    }
  
    this.lineChartLabels = this.chartType === 'monthly' ? moment.months() : moment.weekdays(true);
  
    this.lineChartOptions.tooltips = {
      callbacks: {
        label: tooltipItem => tooltipItem.yLabel + " %"
      }
    };
  
    this.lineChartOptions = { ...this.lineChartOptions };
  }

  updateYearSelection($e) {
    this.yearIsSelected = true;
  }

  updateGroupSelection($e) {
    this.groupIsSelected = true;
  }

  changesInFormControl() {
    this.InputForm1.valueChanges.pipe(distinctUntilChanged()).subscribe(formChanged => {
      if (this.overallStatsSub) this.overallStatsSub.unsubscribe()
      this.getOverallStats(this.GetFormFieldControlYear(), this.GetFormFieldControlGroup())
    })
  }

  private GetFormFieldControlYear() {
    return this.InputForm1.get('year').value;
  }

  private GetFormFieldControlGroup() {
    return this.InputForm1.get('group').value;
  }

  initFormGroup() {
    this.InputForm1 = this._formBuilder.group({
      year: [''],
      group: [{ value: '' }, Validators.required],
    })
  }

  onValueChange($e) {
    this.cdr.detectChanges();
  }

  languageTranslations(): void {
    this.langSub = this.translateService.currentLanguage.pipe(distinctUntilChanged()).subscribe(res => {
      moment.locale(res === 'en' ? 'en-gb' : 'el');
      this.lineChartLabels = this.chartType === 'monthly' ? moment.months() : moment.weekdays(true);
    });
  }

  ngOnDestroy() {
    if (this.chart) this.chart.chart.destroy()
    if (this.langSub) this.langSub.unsubscribe();
    if (this.overallStatsSub) this.overallStatsSub.unsubscribe();
  }
}
